import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getProductTravelList,
  addTravelToProduct,
  updateProductTravel,
  deleteProductTravel,
  getBusinessTravelCountryData,
} from "../../../../../../apis/Product/BusinessTravelApi";
import { getHotelLocationDropdown } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const ProdTravelTable = ({ productId, method = "manual" }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownCountries, setDropdownCountries] = useState([]);
  const [accommodationTypes, setAccommodationTypes] = useState([]);

  // Transportation means options for Flow 14.1
  const transportationMeans = useMemo(
    () => [
      {
        value: t("LAND_TRANSPORTATION_SERVICES"),
        id: "land_transportation",
      },
      {
        value: t("RAILWAY_TRANSPORTATION_SERVICES"),
        id: "railway_transportation",
      },
      {
        value: t("SEA_AND_COASTAL_WATER_TRANSPORTATION_SERVICES"),
        id: "sea_coastal_water",
      },
      {
        value: t("INLAND_WATER_TRANSPORTATION_SERVICES"),
        id: "inland_water",
      },
      {
        value: t("AIR_TRANSPORT_SERVICES"),
        id: "air_transport",
      },
    ],
    [t]
  );

  // All available currency options from all countries
  const currencyOptions = useMemo(() => {
    if (!dropdownCountries || dropdownCountries.length === 0) return [];

    // Extract unique currencies from all countries
    const uniqueCurrencies = [];
    const seenCurrencies = new Set();

    dropdownCountries.forEach((country) => {
      if (country.currency_code && !seenCurrencies.has(country.currency_code)) {
        seenCurrencies.add(country.currency_code);
        uniqueCurrencies.push({
          value: country.currency_name,
          id: country.currency_code,
          currency_code: country.currency_code,
          currency_name: country.currency_name,
          exchange_rate: country.exchange_rate,
          inflation_rate: country.inflation_rate,
          emission_factor: country.emission_factor,
        });
      }
    });

    return uniqueCurrencies.sort((a, b) =>
      a.currency_name.localeCompare(b.currency_name)
    );
  }, [dropdownCountries]);

  // Dynamic placeholders for country to currency auto-selection (with manual override capability)
  const dynamicPlaceholders = useMemo(
    () => ({
      visiting_country: {
        targetField: "currency",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedCountry = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedCountry) {
            // Auto-select currency based on country (user can still manually override later)
            return {
              value: selectedCountry.currency_name,
              id: selectedCountry.currency_code,
              currency_code: selectedCountry.currency_code,
              currency_name: selectedCountry.currency_name,
              exchange_rate: selectedCountry.exchange_rate,
              inflation_rate: selectedCountry.inflation_rate,
              emission_factor: selectedCountry.emission_factor,
            };
          }
          return null;
        },
        dropdownDataKey: "visiting_country",
      },
    }),
    []
  );

  // Function to get currency code for dynamic header display
  const getCurrencyCode = useCallback((rowData) => {
    if (rowData?.currency?.currency_code) {
      return rowData.currency.currency_code;
    }
    if (rowData?.currency?.id) {
      return rowData.currency.id;
    }
    return "";
  }, []);

  // Helper function to extract currency code from various currency object formats
  const extractCurrencyCode = useCallback(
    (currency) => {
      if (!currency) return null;

      // If it's already a currency code (string)
      if (typeof currency === "string") {
        return currency;
      }

      // If it has currency_code property (from auto-selection)
      if (currency.currency_code) {
        return currency.currency_code;
      }

      // If it has id property (from dropdown selection)
      if (currency.id) {
        return currency.id;
      }

      // If it has value property (currency name), map it back to currency code
      if (currency.value) {
        const matchingCurrency = currencyOptions.find(
          (option) =>
            option.value === currency.value ||
            option.currency_name === currency.value
        );
        return matchingCurrency ? matchingCurrency.currency_code : null;
      }

      return null;
    },
    [currencyOptions]
  );

  useEffect(() => {
    getBusinessTravelCountryData({
      setDropdown: setDropdownCountries,
    });
    getHotelLocationDropdown({
      setDropdown: setAccommodationTypes,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const getFieldValue = useCallback((field) => {
    if (!field) return null;
    if (typeof field === "object") {
      // For dropdown fields, use the id if available, otherwise use value
      return field.id || field.value;
    }
    return field;
  }, []);

  // Function to convert identifier back to display object for UI
  const convertIdentifierToDisplay = useCallback(
    (field, fieldName) => {
      if (!field) return null;

      if (fieldName === "means_transportation") {
        const option = transportationMeans.find((item) => item.id === field);
        return option ? { value: option.value, id: option.id } : field;
      }

      if (fieldName === "currency") {
        // Find currency option by currency_code or id
        const option = currencyOptions.find(
          (item) =>
            item.id === field ||
            item.currency_code === field ||
            item.value === field
        );

        return option
          ? { value: option.value, id: option.currency_code }
          : field;
      }

      return field;
    },
    [transportationMeans, currencyOptions]
  );

  const fetchData = useCallback(() => {
    getProductTravelList({
      productId,
      setObjects: (fetchedData) => {
        // Filter data by method
        const filteredData = fetchedData.filter(
          (item) => item.method === method
        );

        // Convert identifiers back to display objects for UI
        const convertedData = filteredData.map((item) => ({
          ...item,
          means_transportation: convertIdentifierToDisplay(
            item.means_transportation,
            "means_transportation"
          ),
          currency: convertIdentifierToDisplay(item.currency, "currency"),
        }));

        setData(convertedData);
      },
    });
  }, [productId, method, convertIdentifierToDisplay]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      // Find the original item to get the current currency if not changed
      const originalItem = data.find((item) => item.id === editedItem.id);

      // If currency is not in editedItem (user didn't touch currency field), use original
      let currencyValue = null;
      if (editedItem.hasOwnProperty("currency")) {
        // User edited currency field
        currencyValue = extractCurrencyCode(editedItem.currency);
      } else {
        // User didn't touch currency field, use original
        currencyValue = extractCurrencyCode(originalItem?.currency);
      }

      // For invoice method, currency is required
      if (method === "invoice" && !currencyValue) {
        console.error(
          "Currency is required for invoice method in ProductTravel"
        );
        return;
      }

      updateProductTravel({
        productId,
        componentId: editedItem.id,
        method: method,
        ...editedItem,
        currency: currencyValue,
        visiting_country: getFieldValue(editedItem.visiting_country),
        means_transportation: getFieldValue(editedItem.means_transportation),
        type_of_accommodation: getFieldValue(editedItem.type_of_accommodation),
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, method, data, setData, getFieldValue, extractCurrencyCode]
  );

  const handleAdd = useCallback(
    (newItem) => {
      // Extract currency code using helper function
      const currencyValue = extractCurrencyCode(newItem.currency);

      // For invoice method, currency is required
      if (method === "invoice" && !currencyValue) {
        console.error(
          "Currency is required for invoice method in ProductTravel"
        );
        return;
      }
      addTravelToProduct({
        productId,
        method: method,
        ...newItem,
        visiting_country: getFieldValue(newItem.visiting_country),
        means_transportation: getFieldValue(newItem.means_transportation),
        type_of_accommodation: getFieldValue(newItem.type_of_accommodation),
        currency: currencyValue,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, method, fetchData, getFieldValue, extractCurrencyCode]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductTravel({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  // Flow 14.1: Invoice Method columns
  const invoiceColumns = useMemo(
    () => [
      {
        accessor: "name_of_journey",
        header: t("NAME_OF_JOURNEY"),
        type: "text",
        disableSortBy: true,
        width: "150px",
      },
      {
        accessor: "visiting_country",
        header: `${t("VISITING_COUNTRY")} *`,
        type: "dropdown",
        dropdownData: dropdownCountries,
        disableSortBy: true,
        width: "120px",
      },
      {
        accessor: "currency",
        header: `${t("CURRENCY")} *`,
        type: "dropdown",
        dropdownData: currencyOptions,
        disableSortBy: true,
        width: "100px",
      },
      {
        accessor: "type_of_accommodation",
        header: t("TYPE_OF_ACCOMMODATION"),
        type: "dropdown",
        dropdownData: accommodationTypes,
        disableSortBy: true,
        width: "150px",
        optional: true,
      },
      {
        accessor: "expenditures_hotel_restaurant",
        header: (props) => {
          // Try to get currency from current row data
          const currencyCode = getCurrencyCode(props?.data?.[0]) || "xx";
          return `${t("EXPENDITURES_HOTEL_RESTAURANT")} * (${currencyCode})`;
        },
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
      },
      {
        accessor: "spacer2",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "means_transportation",
        header: `${t("MEANS_OF_TRANSPORTATION")} *`,
        type: "dropdown",
        dropdownData: transportationMeans,
        disableSortBy: true,
        width: "150px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIP_MEANS_OF_TRANSPORTATION_TRAVEL"),
      },
      {
        accessor: "expenditures_transportation",
        header: (props) => {
          // Try to get currency from current row data
          const currencyCode = getCurrencyCode(props?.data?.[0]) || "xx";
          return `${t("EXPENDITURES_TRANSPORTATION")} * (${currencyCode})`;
        },
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
      },
      {
        accessor: "number_of_employees",
        header: t("NUMBER_OF_EMPLOYEES"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "100px",
      },
    ],
    [
      dropdownCountries,
      currencyOptions,
      transportationMeans,
      accommodationTypes,
      t,
      getCurrencyCode,
    ]
  );

  // Flow 14.2: Manual Method columns
  const manualColumns = useMemo(
    () => [
      {
        accessor: "name_of_journey",
        header: t("NAME_OF_JOURNEY"),
        type: "text",
        disableSortBy: true,
        width: "150px",
      },
      {
        accessor: "visiting_country",
        header: `${t("VISITING_COUNTRY")} *`,
        type: "dropdown",
        dropdownData: dropdownCountries,
        disableSortBy: true,
        width: "120px",
      },
      {
        accessor: "quantity_per_functional_unit",
        header: `${t("QUANTITY_PER_FUNCTIONAL_UNIT")} *`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "150px",
        tipHeader: "Tip",
        tipBody: t("TIP_QUANTITY_PER_FUNCTIONAL_UNIT_TRAVEL"),
      },
      {
        accessor: "manuel",
        header: `${t("MANUAL_KGCO2E")} *`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        tipHeader: "Tip",
        tipBody: t("TIP_MANUAL_KGCO2E_TRAVEL"),
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "100px",
      },
    ],
    [dropdownCountries, t]
  );

  const columns = method === "invoice" ? invoiceColumns : manualColumns;

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      visiting_country: dropdownCountries,
      means_transportation: transportationMeans,
      currency: currencyOptions,
      type_of_accommodation: accommodationTypes,
    }),
    [
      dropdownCountries,
      transportationMeans,
      currencyOptions,
      accommodationTypes,
    ]
  );

  const disableRules = {
    carbon: () => true,
    // Mutually exclusive logic for Flow 14.1 (invoice method)
    expenditures_transportation: (rowData, rowId) => {
      if (method !== "invoice") return false;
      // Disable if hotel/restaurant expenditures has a value
      const hotelRestaurantValue = rowData?.expenditures_hotel_restaurant;
      return (
        hotelRestaurantValue &&
        hotelRestaurantValue !== "" &&
        hotelRestaurantValue !== "0"
      );
    },
    means_transportation: (rowData, rowId) => {
      if (method !== "invoice") return false;
      // Disable if hotel/restaurant expenditures has a value
      const hotelRestaurantValue = rowData?.expenditures_hotel_restaurant;
      return (
        hotelRestaurantValue &&
        hotelRestaurantValue !== "" &&
        hotelRestaurantValue !== "0"
      );
    },
    expenditures_hotel_restaurant: (rowData, rowId) => {
      if (method !== "invoice") return false;
      // Disable if transportation expenditures has a value
      const transportationValue = rowData?.expenditures_transportation;
      return (
        transportationValue &&
        transportationValue !== "" &&
        transportationValue !== "0"
      );
    },
    type_of_accommodation: (rowData, rowId) => {
      if (method !== "invoice") return false;
      // Disable if transportation expenditures has a value
      const transportationValue = rowData?.expenditures_transportation;
      return (
        transportationValue &&
        transportationValue !== "" &&
        transportationValue !== "0"
      );
    },
  };

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProdTravelTable;
